import React from 'react';
import {
  ConfirmDialog, FormField, JalaliDatePicker, NumberInput, Select, Textarea, useToast,
} from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { num } from '../../lib/aiPricingView';
import TaskDialog from '../../components/TaskDialog';
import { ADD_MODEL_PRICE } from './queries';
import { Code } from './shared';

const SOURCES = ['manual', 'openrouter_api'];

/** Today as the picker's `YYYY-MM-DD`, in Iran local time. */
export const todayIso = (now = new Date()) => {
  const tehran = new Date(now.getTime() + (3.5 * 60 + now.getTimezoneOffset()) * 60000);
  return tehran.toISOString().slice(0, 10);
};

/** A picker date → the RFC 3339 instant the contract wants: midnight in Tehran. */
export const dateToInstant = (date) => new Date(`${date}T00:00:00+03:30`).toISOString();

/**
 * Append one price version to a model (flows F2/F3).
 *
 * Prices are never edited in place: a correction is a NEW row with its own
 * `effective_from`, which is what lets every historical call keep pointing at
 * the version that priced it. Back-dating is allowed and asks first, naming
 * the consequence — the calls in `[effective_from, now)` are re-priced and the
 * figures on every report shift — because the operator is about to rewrite
 * history and should know it.
 */
export default function PriceDialog({ open, onOpenChange, model, onDone }) {
  const { t, formatDate } = useTranslation();
  const toast = useToast();

  const [effectiveFrom, setEffectiveFrom] = React.useState(todayIso());
  const [fields, setFields] = React.useState({});
  const [source, setSource] = React.useState('manual');
  const [note, setNote] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [confirmBackdate, setConfirmBackdate] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setEffectiveFrom(todayIso());
    setFields({});
    setSource('manual');
    setNote('');
    setError(null);
    setConfirmBackdate(false);
  }, [open]);

  const set = (key) => (e) => setFields((f) => ({ ...f, [key]: e.target?.value ?? e }));
  const rates = ['inputUsdPerM', 'outputUsdPerM', 'cachedInputUsdPerM', 'audioInputUsdPerM', 'audioUsdPerMinute', 'requestUsd'];
  const hasRate = rates.some((k) => num(fields[k]) != null);
  const dirty = hasRate || !!note || effectiveFrom !== todayIso();
  const backdated = !!effectiveFrom && effectiveFrom < todayIso();

  const validate = (v) => {
    if (!v) return '';
    const n = Number(v);
    if (!Number.isFinite(n) || n < 0) return t('validation.required');
    return '';
  };

  const submit = async () => {
    setPending(true);
    setError(null);
    try {
      const input = {
        modelId: model.modelId,
        effectiveFrom: dateToInstant(effectiveFrom),
        source,
        note: note.trim() || null,
        // Always: a price effective today already covers this morning's calls,
        // and the service bounds the recompute by `[effectiveFrom, now)` itself.
        recompute: true,
      };
      for (const k of rates) input[k] = num(fields[k]) == null ? null : String(fields[k]);
      const d = await gql(ADD_MODEL_PRICE, { input });
      const result = d?.addAiModelPrice;
      toast.success(t('aiPricing.models.price.success'));
      if (result && (result.recomputedCalls || result.recomputedInvocations)) {
        toast.info(t('aiPricing.models.price.recomputed', {
          calls: result.recomputedCalls || 0,
          invocations: result.recomputedInvocations || 0,
        }));
      }
      onOpenChange(false);
      onDone?.(result);
    } catch (e) {
      setError(errorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  const onSubmit = () => {
    if (!hasRate) { setError(t('aiPricing.models.price.needOne')); return; }
    if (backdated) setConfirmBackdate(true); else submit();
  };

  const field = (key, labelKey) => (
    <NumberInput
      label={t(`aiPricing.models.price.${labelKey}`)}
      value={fields[key] || ''}
      onChange={set(key)}
      validate={validate}
      unit={t('common.units.usd')}
      data-testid={`ai-models-price-${key}`}
    />
  );

  return (
    <>
      <TaskDialog
        open={open}
        onOpenChange={onOpenChange}
        title={model ? (
          <span>{t('aiPricing.models.price.title')} <Code>{model.modelId}</Code></span>
        ) : t('aiPricing.models.price.title')}
        error={error}
        pending={pending}
        dirty={dirty}
        submitLabel={t('aiPricing.models.price.submit')}
        submitDisabled={!effectiveFrom}
        onSubmit={onSubmit}
        data-testid="ai-models-price-dialog"
      >
        <FormField label={t('aiPricing.models.price.effectiveFrom')} helperText={t('aiPricing.models.price.effectiveFromHelp')}>
          <div data-testid="ai-models-price-effective">
            <JalaliDatePicker value={effectiveFrom} onChange={setEffectiveFrom} placeholder={t('aiPricing.ledger.filters.datePlaceholder')} />
          </div>
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          {field('inputUsdPerM', 'input')}
          {field('outputUsdPerM', 'output')}
          {field('cachedInputUsdPerM', 'cached')}
          {field('audioInputUsdPerM', 'audioTokens')}
          {field('audioUsdPerMinute', 'audioMinute')}
          {field('requestUsd', 'request')}
        </div>
        <Select
          label={t('aiPricing.models.price.source')}
          value={source}
          onChange={(e) => setSource(e.target?.value ?? e)}
          options={SOURCES.map((s) => ({ value: s, label: t(`aiPricing.models.drawer.source.${s}`) }))}
          placeholder={t('aiPricing.models.price.source')}
        />
        <Textarea
          label={t('aiPricing.models.price.note')}
          value={note}
          onChange={(e) => setNote(e.target?.value ?? e)}
          rows={2}
          maxLength={300}
          showCount
        />
      </TaskDialog>

      <ConfirmDialog
        open={confirmBackdate}
        onOpenChange={setConfirmBackdate}
        title={t('aiPricing.models.price.backdated.title')}
        description={t('aiPricing.models.price.backdated.body', { date: formatDate(dateToInstant(effectiveFrom)) })}
        confirmLabel={t('aiPricing.models.price.backdated.confirm')}
        cancelLabel={t('common.actions.cancel')}
        loading={pending}
        onConfirm={() => { setConfirmBackdate(false); submit(); }}
        data-testid="ai-models-price-backdate"
        confirmTestId="ai-models-price-backdate-confirm"
      />
    </>
  );
}
